import { PLAIN_VIEW, isPlainView } from './plainViewHelper';
import { isBrowser } from './browserHelper';
import {
  NATIVE_APP_OPERATION,
  NATIVE_APP,
  NativeAppParsedQueryParams,
  NativeAppQueryParamKeys,
  getNativeAppQueryParamNames,
  getNativeAppQueryParams,
  isNativeAppView,
  isNativeAppOperation,
} from './nativeAppHelper';

export const OPERATION_SUB_PAGES = {
  CONFIRM: 'confirm',
  CONTACT: 'contact',
  SERVICES: 'services',
  TIME_SLOTS: 'timeslots',
} as const;

export type OperationPath =
  (typeof OPERATION_SUB_PAGES)[keyof typeof OPERATION_SUB_PAGES];

const BOKA_TEST_SERVICE_OPERATION = 'takingataxitothenewtestenvironment';
export const TIMESLOTSTODAY = 'timeslotstoday';

export const isBokaTestServiceURL = () => {
  if (!isBrowser()) return false;
  return window.location.pathname.includes(BOKA_TEST_SERVICE_OPERATION);
};

export const getBokaSubPathname = (
  pathname: string,
  subPath: typeof SUB_PATH,
): string => {
  let prodPath;
  if (isPlainView()) {
    prodPath = `/xx/yy/appointment${subPath}/${PLAIN_VIEW}/`;
  } else if (isNativeAppView()) {
    prodPath = `/xx/yy/appointment${subPath}/${NATIVE_APP}/`;
  } else {
    prodPath = `/xx/yy/appointment${subPath}/`;
  }

  let localPath;
  if (isPlainView()) {
    localPath = `/${PLAIN_VIEW}/`;
  } else if (isNativeAppView()) {
    localPath = `/${NATIVE_APP}/`;
  } else {
    localPath = '/';
  }
  const appBasePath = {
    PROD: prodPath,
    LOCAL: localPath,
  };

  const subPathStartIndex = {
    prod: appBasePath.PROD.length,
    local: appBasePath.LOCAL.length,
  };
  const { prod, local } = subPathStartIndex;
  const subPathIndex = process.env.NODE_ENV === 'production' ? prod : local;
  return pathname.substring(subPathIndex);
};

export const getSubPathsForBoka = (subPath: typeof SUB_PATH): string[] => {
  if (!isBrowser()) return [];
  return getBokaSubPathname(window.location.pathname, subPath)
    .split('/')
    .filter((path) => path !== PLAIN_VIEW && path !== NATIVE_APP);
};

export const isClientEnteringRootPath = (subPath: typeof SUB_PATH) => {
  if (!isBrowser()) return false;
  const subPaths = getSubPathsForBoka(subPath);
  return !subPaths[0] || !subPaths[1];
};

export const isClientEnteringOperationPath = (
  operationPath: OperationPath,
  subPath: typeof SUB_PATH,
) => {
  if (!isBrowser()) return false;
  const subPaths = getSubPathsForBoka(subPath);
  return subPaths[1] === operationPath;
};

export const hasOperationChanged = (
  operation: string,
  subPath: typeof SUB_PATH,
) => {
  if (!isBrowser()) return false;
  const subPaths = getSubPathsForBoka(subPath);
  return !!subPaths[0] && subPaths[0] !== operation;
};

export const getServiceUnavailableUrl = () => {
  const currentUrl = window.location.href.slice(0, -1);
  const lastIndex = currentUrl.lastIndexOf('/');
  const basePath = currentUrl.substring(0, lastIndex);
  const serviceUnavailablePath = getPagePath('service-unavailable');

  return `${basePath}${serviceUnavailablePath}`;
};

const BASE_BUSINESS_OPERATIONS = [
  'athomeservices',
  'b2bkitchenfollowup',
  'benefit_free_space_consultation',
  'benefit_free_space_consultation_remote',
  'benefit_space_solution',
  'business',
  'business_id_service',
  'businessadvanced',
  'businessall',
  'businessdesignconsultation',
  'businessextra',
  'businesshome',
  'businessinteriordesign',
  'businessnetwork',
  'businessremote',
  'businesstips',
  'businesstore',
  'coworkerbookedservicesbusiness',
  'coworkingbusiness',
  'designcheck',
  'designconsultingbusiness',
  'designservicebusiness',
  'externalbusiness',
  'ibn',
  'ibnconsultation',
  'ibnextra',
  'initialmeetingbusiness',
  'kitchenbusiness',
  'onlinebusiness',
  'remotebusiness',
  'servicesb2b',
  'storebusiness',
];

const BASE_OPERATIONS = [
  NATIVE_APP_OPERATION,
  BOKA_TEST_SERVICE_OPERATION,
  TIMESLOTSTODAY,
  'advice',
  'aftersales',
  'assistedshopping',
  'bathroom',
  'bathroomextra',
  'bathroomstore',
  'bedroom',
  'bedroomextra',
  'besta',
  'birthday',
  'buyback',
  'childrensroom',
  'childroom',
  'cleanenergy',
  'completedesign',
  'consultation',
  'consulting',
  'consultingdesign',
  'coworkerbookedservices',
  'coworkerbookedserviceskitchen',
  'design',
  'designbasic',
  'designcomplete',
  'designcustomer',
  'designextra',
  'designhome',
  'designremote',
  'designstore',
  'energyservices',
  'expertcheck',
  'event',
  'finalization',
  'finance',
  'fleamarket',
  'flex',
  'funday',
  'gongi',
  'homecoach',
  'homedesign',
  'homeplanning',
  'homesmart',
  'ifpricetest',
  'individual',
  'initialmeeting',
  'instorebedroomextra',
  'instorelivingroom',
  'instorewardrobe',
  'interiordesign',
  'interiordesignvalidation',
  'internal',
  'internalservices',
  'internaluseonly',
  'kitchen',
  'kitchen_eec_dda_aed_bdf',
  'kitchen_sf_trial',
  'kitchenadditional',
  'kitchencheck',
  'kitchenconsultation',
  'kitchenexpertcheck',
  'kitchenextra',
  'kitchenfinalizeremote',
  'kitchenfinalizestore',
  'kitchenfollowup',
  'kitcheninhome',
  'kitcheninternal',
  'kitchenmeasurement',
  'kitchenmeasurementplanning',
  'kitchenonline',
  'kitchenparts',
  'kitchenplan',
  'kitchenplanning',
  'kitchenplanningall',
  'kitchenremote',
  'kitchenrenewal',
  'kitchenstorage',
  'kitchenstore',
  'livingroom',
  'livingroomcomplete',
  'livingroomextra',
  'mattress',
  'measure',
  'measure_experiment', // test operation area for RS market
  'measurea',
  'measureb',
  'organize',
  'pax',
  'paxvalidation',
  'planningathome',
  'rcmp',
  'recycle',
  'remotelivingroom',
  'remotemeasurement',
  'remoteservices',
  'remotesofa',
  'remotewardrobe',
  'reservation',
  'seasonal',
  'servicedesign',
  'shopper',
  'signlanguagesupport',
  'sleep',
  'sleepevent',
  'smarthome',
  'sofa',
  'storage',
  'testservice',
  'trailer',
  'transportservices',
  'upcomingwaves',
  'verification',
  'wardrobe',
  'workspace',
  'worktopplanning',
];

const VALID_PAGES = ['questions', 'service-unavailable', 'questions-confirmed'];

const EXTERNAL_QUERY_PARAMS = {
  VPC: 'vpc',
  PROJECT_ID: 'projectid',
  COOKIE_CONSENT_OVERRIDE: 'cookieconsent-override',
};

const BOKA_QUERY_PARAMS = {
  TOKEN: 'token',
  ZIP: 'zip',
  SERVICE: 'service',
  OPERATION: 'operation',
  STORE: 'store',
  REFAPP: 'ref_app',
  STORESELECTOR: 'storeselector',
};

const isPageNameValid = (page: string): boolean => {
  return VALID_PAGES.includes(page);
};

export const isBusinessOperation = (
  operation: string,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  const toNormalOperations = appConfig.CONVERTTONORMALOPERATIONS;
  const businessOperations = BASE_BUSINESS_OPERATIONS.filter(
    (businessOperation) => !toNormalOperations.includes(businessOperation),
  );
  return businessOperations.includes(operation);
};

const isValidBokaOperation = (
  operation: string,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  const operations = [
    ...BASE_OPERATIONS,
    ...appConfig.CONVERTTONORMALOPERATIONS,
  ];
  return (
    operations.includes(operation) || isBusinessOperation(operation, appConfig)
  );
};

const isValidPageRequest = (
  appConfig: typeof CSS_CONFIG,
  subPath: typeof SUB_PATH,
  zip?: string,
): boolean => {
  const [page, operationSubPage] = getSubPathsForBoka(subPath);
  if (page.length === 0) return true;
  if (isPageNameValid(page) || isPageNameValid(operationSubPage)) return true;

  const isValidOperationPage = isValidBokaOperation(page, appConfig);
  if (isValidOperationPage && operationSubPage && !zip) return false;

  return isValidOperationPage;
};

export const shouldRedirect = (
  appConfig: typeof CSS_CONFIG,
  subPath: typeof SUB_PATH,
  zip?: string,
): boolean => {
  return !isValidPageRequest(appConfig, subPath, zip);
};

interface ParsedQueryParams extends NativeAppParsedQueryParams {
  questionsToken: string;
  zip: string;
  service: string;
  vpc: string;
  projectId: string;
  operation: string;
  store: string;
  refApp: string;
  storeSelector: boolean;
}

export const getQueryParamsFromURL = (): ParsedQueryParams => {
  const { SERVICE, ZIP, TOKEN, OPERATION, STORE, REFAPP, STORESELECTOR } =
    BOKA_QUERY_PARAMS;
  const { VPC, PROJECT_ID } = EXTERNAL_QUERY_PARAMS;
  const queryString = isBrowser() ? window.location.search : '';
  const queryParams = new URLSearchParams(queryString);

  const questionsToken = queryParams.get(TOKEN) || '';
  const zip = queryParams.get(ZIP) || '';
  const service = queryParams.get(SERVICE) || '';
  const vpc = queryParams.get(VPC) || '';
  const projectId = queryParams.get(PROJECT_ID) || '';
  const operation = queryParams.get(OPERATION) || '';
  const store = queryParams.get(STORE) || '';
  const refApp = queryParams.get(REFAPP) || '';
  const storeSelector =
    queryParams.get(STORESELECTOR) === 'false' ? false : true;
  const nativeAppParams = getNativeAppQueryParams();

  return {
    questionsToken,
    zip,
    service,
    vpc,
    projectId,
    operation,
    store,
    refApp,
    storeSelector,
    ...nativeAppParams,
  };
};

type PersistingQueryParamKeys =
  | 'vpc'
  | 'projectid'
  | 'cookieconsent-override'
  | 'storeselector'
  | NativeAppQueryParamKeys;

const filterQueryParams = (
  queryParams: URLSearchParams,
  exclude: PersistingQueryParamKeys[],
) => {
  const keys = [...queryParams.keys()];
  const persistingQueryParams = [
    ...Object.values(EXTERNAL_QUERY_PARAMS),
    BOKA_QUERY_PARAMS.STORESELECTOR,
    ...getNativeAppQueryParamNames(),
  ];

  keys.forEach((key) => {
    if (
      !persistingQueryParams.includes(key) ||
      (exclude as string[]).includes(key)
    ) {
      queryParams.delete(key);
    }
  });
};

type AddQueryParamsToPathOptions = {
  addToExistingQueryParam?: boolean;
  exclude?: PersistingQueryParamKeys[];
};

export const addPersistingQueryParamsToPath = (
  path: string,
  options?: AddQueryParamsToPathOptions,
): string => {
  if (!isBrowser()) {
    return path;
  }

  const { addToExistingQueryParam = false, exclude = [] } = options || {};
  const queryParams = new URLSearchParams(window.location.search);
  filterQueryParams(queryParams, exclude);
  const queryParamsString = queryParams.toString();

  return queryParamsString
    ? `${path}${addToExistingQueryParam ? '&' : '?'}${queryParamsString}`
    : path;
};

export const getPrevPath = (operation: string): string => {
  const currentUrl = window.location.pathname;

  let prevPage = '';
  if (currentUrl.endsWith('/contact/')) {
    prevPage = 'timeslots/';
  } else if (currentUrl.endsWith('/timeslots/')) {
    prevPage = 'services/';
  } else if (currentUrl.endsWith('/services/')) {
    prevPage = '';
  }

  let path = '';
  if (isPlainView()) {
    path = `/${PLAIN_VIEW}/${operation}/${prevPage}`;
  } else if (isNativeAppView()) {
    path = `/${NATIVE_APP}/${operation}/${prevPage}`;
  } else {
    path = `/${operation}/${prevPage}`;
  }
  return path;
};

export const getPath = (operation: string, step: string): string => {
  if (isPlainView()) {
    return `/${PLAIN_VIEW}/${operation}/${step}/`;
  } else if (isNativeAppView()) {
    return `/${NATIVE_APP}/${operation}/${step}/`;
  } else {
    return `/${operation}/${step}/`;
  }
};

/* ikeaapp cleanup: Remove NATIVE_APP_OPERATION once the app team starts using /app instead of /ikeaapp */
export const getPagePath = (page: string): string => {
  if (isPlainView()) {
    return `/${PLAIN_VIEW}/${page}/`;
  } else if (isNativeAppOperation()) {
    return `/${NATIVE_APP_OPERATION}/${page}/`;
  } else if (isNativeAppOperation()) {
    if (page === NATIVE_APP_OPERATION) {
      return `/${NATIVE_APP_OPERATION}/`;
    }
    return `/${NATIVE_APP_OPERATION}/${page}/`;
  } else if (isNativeAppView()) {
    return `/${NATIVE_APP}/${page}/`;
  } else {
    return `/${page}/`;
  }
};
